import { TUpdateAction } from "@fl/shared/src/types/actions";
import { GerenciadorPaginasItens } from "@fl/shared/src/types/menus";
import { useRef, useState } from "react";
import {
  BiCheckbox,
  BiCheckSquare,
  BiChevronDown,
  BiChevronRight,
  BiCog,
  BiMoveVertical,
  BiSolidTrash,
  BiTrash,
} from "react-icons/bi";
import { FaEdit, FaRegEdit } from "react-icons/fa";
import { MdOutlineNewReleases } from "react-icons/md";

import { IoMdAddCircle, IoMdAddCircleOutline } from "react-icons/io";
import StringsFuncs from "../../../../utils/stringsFuncs";
import "../styles.css";
interface IProps {
  item: GerenciadorPaginasItens;
  subelements?: JSX.Element[];
  children: boolean;
  onCheck: (item: GerenciadorPaginasItens) => void;
  onCollapse: (item: GerenciadorPaginasItens) => void;
  onUpdate: (item: GerenciadorPaginasItens, action: TUpdateAction) => void;
  onUpdateDrag: (
    draggedItemId: number,
    targetItemId: number | null,
    position: number
  ) => void;
  showCollapse?: boolean;
  showOrder?: boolean;
  showChecked?: boolean;
  showAction?: boolean;
}

const MenuToolManagerItem: React.FC<IProps> = ({
  item,
  subelements,
  children,
  onCheck,
  onCollapse,
  onUpdate,
  onUpdateDrag,
  showCollapse,
  showOrder,
  showChecked,
  showAction,
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverItem, setDragOverItem] = useState<{
    id_pagina_menu: number;
    position: number; // 0 = TOP, 1 = INSIDE, 2 = BOTTOM
  } | null>(null);
  const [size, setSize] = useState(20);
  const [hoverAdd, setHoverAdd] = useState(false);
  const [hoverRemove, setHoverRemove] = useState(false);
  const [hoverEdit, setHoverEdit] = useState(false);

  const onUpdateCheck = (node: GerenciadorPaginasItens, checked: boolean) => {
    const updated = { ...node };
    updated.checked = checked;
    onCheck(updated);
  };

  const onUpdateCollapse = (
    node: GerenciadorPaginasItens,
    collapsed: boolean
  ) => {
    const updated = { ...node };
    updated.collapsed = collapsed;
    onCollapse(updated);
  };

  const handleDragStart = (e: React.DragEvent<HTMLLIElement>) => {
    e.stopPropagation();
    e.dataTransfer.setData("text/plain", item.id_pagina_menu?.toString() || "");
  };

  const handleDragOver = (e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const sizeParts = e.currentTarget.offsetHeight / 3;
    const currentPos = e.nativeEvent.offsetY;
    var position = -1; //0 TOP, 1 INSIDE, 2 BOTTOM
    if (currentPos < sizeParts) {
      position = 0;
    } else if (currentPos >= sizeParts && currentPos < 2 * sizeParts) {
      position = 1;
    } else {
      position = 2;
    }

    setDragOverItem({ id_pagina_menu: item.id_pagina_menu, position });
  };

  const handleDrop = (e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const draggedItemId = e.dataTransfer.getData("text/plain");

    if (draggedItemId) {
      const position = dragOverItem?.position || 1; // Default para `inside`
      onUpdateDrag(
        parseInt(draggedItemId, 10),
        item.id_pagina_menu || null,
        position // TOP = before
      );
    }

    setDragOverItem(null);
  };

  const handleIconClick = (e: React.MouseEvent<SVGElement, MouseEvent>) => {
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
    setDragOverItem(null);
  };

  return (
    <li
      ref={inputRef}
      draggable={isDragging}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
      key={
        item.id_pagina_menu
          ? item.id_pagina_menu.toString()
          : new StringsFuncs().generateCode()
      }
    >
      <div
        className={`drag-item ${
          dragOverItem && dragOverItem.id_pagina_menu === item.id_pagina_menu
            ? dragOverItem.position === 0
              ? "drag-top"
              : dragOverItem.position === 1
                ? "drag-inside"
                : "drag-bottom"
            : children
              ? "tree-item-children"
              : "tree-item"
        }`}
      >
        <div className="icons">
          {showCollapse && (
            <div className="icons icon-collapse">
              {item.children.length > 0 ? (
                item.collapsed ? (
                  <BiChevronRight
                    className="pointer"
                    size={size}
                    onMouseMoveCapture={() => setSize(22)}
                    onMouseLeave={() => setSize(20)}
                    onClick={() => onUpdateCollapse(item, false)}
                  />
                ) : (
                  <BiChevronDown
                    className="pointer"
                    size={size}
                    onMouseMoveCapture={() => setSize(22)}
                    onMouseLeave={() => setSize(20)}
                    onClick={() => onUpdateCollapse(item, true)}
                  />
                )
              ) : (
                <></>
              )}
            </div>
          )}

          {showOrder && (
            <BiMoveVertical
              onMouseDown={handleIconClick}
              className="move"
              size={15}
            />
          )}

          {showChecked ? (
            item.checked ? (
              <BiCheckSquare
                className="pointer"
                size={20}
                onClick={() => onUpdateCheck(item, false)}
              />
            ) : (
              <BiCheckbox
                className="pointer"
                size={20}
                onClick={() => onUpdateCheck(item, true)}
              />
            )
          ) : null}

          {item.config && !children && (
            <BiCog size={15} className="item-small-space" color="indianred" />
          )}

          {item.new && (
            <MdOutlineNewReleases
              size={15}
              className="item-small-space"
              color="#0dbb19"
            />
          )}
        </div>

        <div className={`tree-text ${!children && "tree-text-high"}`}>
          {`${item.ordem} - ${item.pagina}`}
        </div>

        {showAction && (
          <div className="icons icon-actions">
            <div
              className="icons icon-space"
              onMouseMoveCapture={() => setHoverAdd(true)}
              onMouseLeave={() => setHoverAdd(false)}
              title={`Adicionar Subitem à ${item.pagina}`}
            >
              {hoverAdd ? (
                <IoMdAddCircle
                  className="pointer"
                  size={20}
                  color="#11610c"
                  onClick={() => {
                    onUpdate(item, "add");
                  }}
                />
              ) : (
                <IoMdAddCircleOutline className="pointer" size={20} />
              )}
            </div>

            <div
              className="icons icon-space"
              onMouseMoveCapture={() => setHoverRemove(true)}
              onMouseOut={() => setHoverRemove(false)}
              title={`Remover ${item.pagina} (e subitems)`}
            >
              {hoverRemove ? (
                <BiSolidTrash
                  className="pointer"
                  size={20}
                  color="#d41c37"
                  onClick={() => {
                    onUpdate(item, "delete");
                  }}
                />
              ) : (
                <BiTrash className="pointer" size={20} />
              )}
            </div>

            <div
              className="icons icon-space"
              onMouseMoveCapture={() => setHoverEdit(true)}
              onMouseLeave={() => setHoverEdit(false)}
              title={`Alterar ${item.pagina}`}
            >
              {hoverEdit ? (
                <FaEdit
                  className="pointer"
                  size={20}
                  color="#1c91d4"
                  onClick={() => {
                    onUpdate(item, "update");
                  }}
                />
              ) : (
                <FaRegEdit className="pointer" size={20} />
              )}
            </div>
          </div>
        )}
      </div>

      {!item.collapsed && subelements && <ul>{subelements}</ul>}
    </li>
  );
};
export default MenuToolManagerItem;
